import type { ShouldRevalidateFunction } from 'react-router';

const PROFESSIONAL_BASE = '/professional';

/**
 * Extract the `:urlSegment` from a `/professional/<seg>/...` pathname, or
 * `null` if the path is outside the professional layout.
 */
export const getProfessionalUrlSegment = (pathname: string): string | null => {
    if (
        pathname !== PROFESSIONAL_BASE &&
        !pathname.startsWith(`${PROFESSIONAL_BASE}/`)
    ) {
        return null;
    }
    const rest = pathname.slice(PROFESSIONAL_BASE.length + 1);
    if (!rest) {
        return null;
    }
    return rest.split('/')[0] || null;
};

/**
 * Build a `shouldRevalidate` policy that skips revalidation when the SPA
 * navigation stays inside the same layout instance (per `matches`). Form
 * submissions are evaluated by the same URL check so a non-GET redirect that
 * crosses professionals still revalidates layout-specific data.
 */
const skipRevalidationWhile =
    (
        matches: (current: string, next: string) => boolean,
    ): ShouldRevalidateFunction =>
    ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
        if (matches(currentUrl.pathname, nextUrl.pathname)) {
            return false;
        }
        return defaultShouldRevalidate;
    };

/** Same `/professional/<seg>` (regardless of which tab is mounted). */
const isSameProfessional = (a: string, b: string): boolean => {
    const seg = getProfessionalUrlSegment(a);
    return seg !== null && seg === getProfessionalUrlSegment(b);
};

/** Same `/professional/<seg>/info(/...)` — both paths under the info subtree. */
const isSameProfessionalInfo = (a: string, b: string): boolean => {
    const seg = getProfessionalUrlSegment(a);
    if (seg === null || seg !== getProfessionalUrlSegment(b)) {
        return false;
    }
    const infoPrefix = `${PROFESSIONAL_BASE}/${seg}/info`;
    const isUnderInfo = (p: string) =>
        p === infoPrefix || p.startsWith(`${infoPrefix}/`);
    return isUnderInfo(a) && isUnderInfo(b);
};

/** About-tab pagination that the info layout loader actually reads. */
const INFO_PAGINATION_PARAMS = ['teamPage', 'awardPage'] as const;

const infoPaginationChanged = (currentUrl: URL, nextUrl: URL): boolean =>
    INFO_PAGINATION_PARAMS.some(
        (param) =>
            currentUrl.searchParams.get(param) !==
            nextUrl.searchParams.get(param),
    );

/**
 * `_app.professional.$urlSegment.tsx` is a layout shared by every tab for a
 * single professional. Skip revalidation when SPA navigation stays on the
 * same professional so RR7 single-fetch does not re-request layout data the
 * leaf-only `.data` response will not include. Navigating to a DIFFERENT
 * professional still revalidates, since the layout's data is
 * professional-specific.
 */
export const shouldRevalidateProfessionalLayout: ShouldRevalidateFunction =
    skipRevalidationWhile(isSameProfessional);

/**
 * Same protection for the nested `info` sub-layout: skip revalidation when
 * navigation stays under `/professional/<seg>/info/...` for the same
 * professional (info index <-> team-members <-> testimonials).
 *
 * Exception: `teamPage` / `awardPage` search-param changes must revalidate.
 * The About tab's "View more" buttons only update those params, and the
 * team/awards UI reads this layout's loader data — skipping would leave the
 * first page on screen even though the URL advanced.
 */
export const shouldRevalidateProfessionalInfoLayout: ShouldRevalidateFunction =
    ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
        if (infoPaginationChanged(currentUrl, nextUrl)) {
            return true;
        }
        if (isSameProfessionalInfo(currentUrl.pathname, nextUrl.pathname)) {
            return false;
        }
        return defaultShouldRevalidate;
    };

const searchParamsEqualIgnoring = (
    current: URLSearchParams,
    next: URLSearchParams,
    ignored: readonly string[],
): boolean => {
    const keys = new Set([...current.keys(), ...next.keys()]);
    for (const key of ignored) {
        keys.delete(key);
    }
    for (const key of keys) {
        if (current.get(key) !== next.get(key)) {
            return false;
        }
    }
    return true;
};

/**
 * Videos leaf: keep fetching on sort/page (the loader reads those), but skip
 * when only `?video=` changes. Play/close just opens a dialog over the same
 * list — a refetch remounts the grid and resets scroll to the top.
 */
export const shouldRevalidateProfessionalVideosLeaf: ShouldRevalidateFunction =
    ({ currentUrl, nextUrl, formAction }) => {
        if (formAction) {
            return false;
        }
        if (
            currentUrl.pathname === nextUrl.pathname &&
            searchParamsEqualIgnoring(
                currentUrl.searchParams,
                nextUrl.searchParams,
                ['video'],
            )
        ) {
            return false;
        }
        return true;
    };
